import { MockedRequest, ResponseResolver, restContext, RestRequest } from 'msw';
import { delayedResponse } from '@/utils/useMswCustomResponse';
import { MONTHLY_APPLICATION_STATUS } from '../../constants/monthlyApplications';
import { choiceRandom } from '@/utils/useHelper';
import dayjs from 'dayjs';
import {
  MonthlyApplication,
  MonthlyApplicationDetail,
  MonthlyApplications,
  ApplicationStatus,
} from '../types/monthlyApplication';
import { ApplicationSuggestions } from '../types/application';

/**
 * 全ての契約ステータスのモックを生成
 */
const generateMockApplications = (
  page: number,
  limit: number,
): MonthlyApplications['applications'] => {
  const currentDateString = new Date().toString();

  const applications = (
    Object.keys(MONTHLY_APPLICATION_STATUS) as ApplicationStatus[]
  ).map<MonthlyApplication>((statusKey, i) => ({
    id: i + 1,
    status: statusKey as keyof typeof MONTHLY_APPLICATION_STATUS,
    statusLabel:
      MONTHLY_APPLICATION_STATUS[
        statusKey as keyof typeof MONTHLY_APPLICATION_STATUS
      ].label,
    parkingId: 100 + i,
    parkingName: `テスト駐車場 ${i}`,
    garageNumber: i,
    cardId: '7L6GMKZC043DG8W4FRMZF45LX2',
    closeDate: '2025-05-08',
    initialPayment: {
      contractAmount: 10000,
      depositAmount: 10000,
      initialMonthlyAmount: 10000,
      monthlyAmount: 10000,
      proratedAmount: 10000,
      totalAmount: 10000,
    },
    parkingAddress: '東京都新宿区',
    applicantLastName: '田中',
    applicantFirstName: '太郎',
    applicantLastNameKana: 'タナカ',
    applicantFirstNameKana: 'タロウ',
    renewalPayment: {
      dueDate: '2025-05-08',
      renewalAmount: 10000,
    },
    scheduledStartDate: '2025-05-08',
    unpaidPayment: {
      dueDate: '2025-05-08',
      targetMonth: '5',
      unpaidAmount: 10000,
    },
    appliedAt: currentDateString,
    requestedStartDate:
      statusKey === MONTHLY_APPLICATION_STATUS.APPLICATION_PENDING.ID
        ? '最短希望'
        : currentDateString,
    contractStartDate: currentDateString,
    contractUpdateDate: currentDateString,
    applicationDueDate: currentDateString,
    contractEndDate: currentDateString,
    contractSignedAt: currentDateString,
    rejectedAt: currentDateString,
    closedAt: currentDateString,
    canPayInitialPayment: (
      [
        MONTHLY_APPLICATION_STATUS.APPLICATION_INITIAL_PAYMENT_PENDING.ID,
      ] as ApplicationStatus[]
    ).includes(statusKey),
    canUpdateContract: false,
    canUpdatePaymentMethod: false,
    canSubmitApplication: false,
    canCancelContract: false,
    canCancelApplication: false,
    hasResubmissionRequests: [
      'PROVISIONAL_APPLICATION_RESUBMISSION_REQUESTED',
      'APPLICATION_RESUBMISSION_REQUESTED',
    ].includes(statusKey),
    hasUnsubmittedImages: true,
    hasPaymentIssues: true,
    canRenewContract:
      (
        [
          MONTHLY_APPLICATION_STATUS.CONTRACT_IN_PROGRESS.ID,
        ] as ApplicationStatus[]
      ).includes(statusKey) && choiceRandom([true, false]),
    requestedCloseDate: currentDateString,
    scheduledCloseDate: currentDateString,
  }));

  const offset = (page - 1) * limit;

  return applications.slice(offset, offset + limit);
};

// 貸出一覧を取得する
const getMonthlyApplications: ResponseResolver<
  MockedRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const url = new URL(req.url);
  const page = Number(url.searchParams.get('page')) || 1;
  const limit = Number(url.searchParams.get('limit')) || 10;

  const totalItems = 50;
  const totalPages = Math.ceil(totalItems / limit);

  return delayedResponse(
    ctx.status(200),
    ctx.json<MonthlyApplications>({
      applications: generateMockApplications(page, limit),
      pagination: {
        total: totalItems,
        perPage: limit,
        currentPage: page,
        lastPage: totalPages,
        firstPageUrl: '',
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
      },
    }),
  );
};

// 貸出契約詳細を取得する
const getMonthlyApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  if (!applicationId) return delayedResponse(ctx.status(404));

  const currentDateString = new Date().toString();

  /** applicationId に基づいた契約ステータス */
  const status =
    MONTHLY_APPLICATION_STATUS[
      Object.keys(MONTHLY_APPLICATION_STATUS)[
        applicationId - 1
      ] as ApplicationStatus
    ];

  const initialPayment = {
    canPay: true,
    contractAmount: 10000,
    depositAmount: 50000,
    initialMonthlyAmount: 10000,
    monthlyAmount: 20000,
    proratedAmount: 10000,
    proratedDays: 15,
    targetMonth: dayjs().format('YYYY-MM'),
    totalAmount: 100000,
  };

  const renewalPayment = {
    canPay: true,
    dueDate: dayjs().add(11, 'month').toString(),
    renewalAmount: 10000,
  };

  const unpaidPayment = {
    canPay: true,
    dueDate: dayjs().add(10, 'day').toString(),
    targetMonth: dayjs().format('YYYY-MM'),
    unpaidAmount: 20000,
  };

  const application: MonthlyApplicationDetail = {
    applicationDueDate: dayjs().add(14, 'day').toString(),
    appliedAt: currentDateString,
    cardId: '7L6GMKZC043DG8W4FRMZF45LX2',
    closedAt: dayjs().subtract(1, 'day').toString(),
    closeDate: dayjs().add(10, 'day').toString(),
    contractEndDate: dayjs().add(1, 'year').toString(),
    contractSignedAt: dayjs().subtract(5, 'day').toString(),
    contractStartDate: dayjs().subtract(10, 'day').toString(),
    garageNumber: 101,
    id: applicationId,
    parkingAddress: '東京都渋谷区道玄坂1-2-3',
    parkingId: 5678,
    parkingName: '渋谷パーキング',
    rejectedAt: dayjs().subtract(2, 'day').toString(),
    monthlyAmount: 50000,
    initialPayment,
    renewalPayment,
    unpaidPayment,
    requestedCloseDate: dayjs().subtract(3, 'day').toString(),
    requestedStartDate: dayjs().subtract(7, 'day').toString(),
    scheduledStartDate: dayjs().toString(),
    status: status.ID,
    statusLabel: status.label,
    cancellationDateRange: {
      minDate: '2025-05-01',
      maxDate: '2025-10-01',
    },
  };

  if (!application)
    return delayedResponse(
      ctx.status(400),
      ctx.json({
        messages: ['本申込が存在しません'],
        code: 100,
      }),
    );

  return delayedResponse(
    ctx.status(200),
    ctx.json<MonthlyApplicationDetail>(application),
  );
};

// 本申込の入力補完を取得する
const getApplicationSuggestions: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);

  const usagePurpose = choiceRandom(['PERSONAL', 'CORPORATION'] as const);

  return delayedResponse(
    ctx.status(200),
    ctx.json<ApplicationSuggestions>({
      contractStartDate: '2025-05-01',
      dueDate: '2025-04-30T23:59:59',
      garageImageUrl: 'https://picsum.photos/700/500',
      genderMapping: [
        { enum: 'MAN', label: '男' },
        { enum: 'WOMAN', label: '女' },
      ],
      parkingAddress: '東京都渋谷区道玄坂1-2-3',
      parkingName: '渋谷パーキング',
      selectableGarages: [
        {
          contractAmount: 10000,
          depositAmount: 50000,
          garageId: 1,
          garageNumber: 101,
          monthlyAmount: 20000,
          renewalAmount: 5000,
          content: null,
          type: 'STANDARD',
          typeLabel: '普通',
        },
        {
          contractAmount: 15000,
          depositAmount: 60000,
          garageId: 2,
          garageNumber: 102,
          monthlyAmount: 25000,
          renewalAmount: 6000,
          content: '屋根付き',
          type: 'SPECIAL',
          typeLabel: '特殊区画',
        },
      ],
      usagePurpose,
      usagePurposeLabel: usagePurpose === 'PERSONAL' ? '個人利用' : '法人利用',
      workplaceIndustryCategoryMapping: [
        { enum: 'CONSTRUCTION', label: '建設業' },
      ],
      overrideMonthlyAmount: choiceRandom([null, 55000]),
      jobTypeMapping: [
        { enum: 'COMPANY_OFFICER', label: '会社役員' },
        { enum: 'COMPANY_EMPLOYEE', label: '会社員' },
        { enum: 'SELF_EMPLOY', label: '自営業' },
        { enum: 'PART_TIME_JOB', label: 'パート・アルバイト' },
        { enum: 'STUDENT', label: '学生' },
        { enum: 'HOMEWORKER_OTHER', label: '家事従事者/その他' },
      ],
    }),
  );
};

// 本申込申請
const postApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);

  return delayedResponse(ctx.status(204));
};

/**
 * 解約を申請
 */
const postCloseApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const body = await req.json();
  if (!body['closeDate']) {
    return delayedResponse(
      ctx.status(422),
      ctx.json({
        messages: ['エラー'],
      }),
    );
  }
  return delayedResponse(ctx.status(204));
};

const cancelApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  return delayedResponse(ctx.status(204));
};

const getContractDocument: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  if (!applicationId) {
    return delayedResponse(ctx.status(404));
  }
  return delayedResponse(
    ctx.status(200),
    ctx.body('https://example.com/contract.pdf'),
  );
};

const mockApplication = {
  getMonthlyApplication,
  getMonthlyApplications,
  getApplicationSuggestions,
  postApplication,
  postCloseApplication,
  cancelApplication,
  getContractDocument,
};

export default mockApplication;
